'use client';

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getSubmissions, updateSubmission } from '../../redux/actions/submissionActions';
import { getOrders, updateOrder } from '../../redux/actions/orderActions';
import { getUsers } from '../../redux/actions/userActions';
import { formatDateFR } from '../../utils/dateFormatter';

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const { submissions } = useSelector(state => state.submissions);
  const { orders } = useSelector(state => state.orders);
  const { users } = useSelector(state => state.users);
  const [dateRange] = useState({
    from: '',
    to: ''
  });
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalSubmissions: 0,
    pendingSubmissions: 0,
    totalOrders: 0,
    activeOrders: 0,
    completedOrders: 0
  });

  // Add these state variables
  const [statusFilter] = useState('');
  const [currentPage] = useState(1);
  const [userFilter] = useState('');
  const [showActionModal, setShowActionModal] = useState(false);
  const [submissionToAction, setSubmissionToAction] = useState(null);
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const [actionReason, setActionReason] = useState('');

  // Function to open the action modal
  const handleOpenSubmissionAction = (submission, type) => {
    setSubmissionToAction(submission);
    setActionType(type);
    setActionReason('');
    setShowActionModal(true);
  };

  // Function to handle the submission action
  const handleSubmissionAction = async () => {
    if (!submissionToAction) return;

    try {
      const updatedData = {
        status: actionType === 'approve' ? 'approved' : 'rejected',
        adminNotes: actionReason
      };

      await dispatch(updateSubmission(submissionToAction.id, updatedData));
      setShowActionModal(false);

      // Reload dashboard data
      dispatch(getSubmissions({ limit: 5 }));
    } catch (error) {
      console.error('Erreur lors de l\'action sur la soumission:', error);
    }
  };

  // Order status handlers
  const handleOrderStatusChange = async (orderId, newStatus) => {
    try {
      await dispatch(updateOrder(orderId, { status: newStatus }));
      // Rafraîchir la liste des commandes
      dispatch(getOrders({
        page: currentPage,
        limit: 10,
        status: statusFilter || undefined,
        dateFrom: dateRange.from || undefined,
        dateTo: dateRange.to || undefined,
        userId: userFilter || undefined
      }));
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
    }
  };

  useEffect(() => {
    // Charger les utilisateurs récents
    dispatch(getUsers({ limit: 5 }));

    // Charger les soumissions récentes
    dispatch(getSubmissions({ limit: 5 }));

    // Charger les commandes récentes
    dispatch(getOrders({ limit: 5 }));
  }, [dispatch]);

  // Calculer les statistiques
  useEffect(() => {
    const allUsers = users?.items || [];
    const allSubmissions = submissions?.items || [];
    const allOrders = orders?.items || [];

    setStats({
      totalUsers: users?.pagination?.total || allUsers.length,
      activeUsers: allUsers.filter(u => u.emailVerified).length,
      pendingUsers: allUsers.filter(u => !u.emailVerified).length,
      totalSubmissions: submissions?.pagination?.total || allSubmissions.length,
      pendingSubmissions: allSubmissions.filter(s => s.status === 'pending').length,
      approvedSubmissions: allSubmissions.filter(s => s.status === 'approved').length,
      rejectedSubmissions: allSubmissions.filter(s => s.status === 'rejected').length,
      totalOrders: orders?.pagination?.total || allOrders.length,
      activeOrders: allOrders.filter(o => ['pending', 'accepted', 'in_progress'].includes(o.status)).length,
      completedOrders: allOrders.filter(o => o.status === 'completed').length,
      sentOrders: allOrders.filter(o => o.status === 'sent').length
    });
  }, [users, submissions, orders]);

  return (
    <div className="admin-dashboard-container">
      <div className="page-header">
        <h1>Tableau de bord administrateur</h1>
        <div className="header-actions">
          <Link to="/admin/users/new" className="btn btn-primary">
            <i className="material-icons">person_add</i>
            Ajouter un utilisateur
          </Link>
          <Link to="/admin/users/invitations" className="btn btn-outline-primary">
            <i className="material-icons">mail</i>
            Gérer les invitations
          </Link>
        </div>
      </div>

      <div className="stats-overview">
        {/* Carte statistiques utilisateurs */}
        <div className="stats-card">
          <div className="stats-icon">
            <i className="material-icons">people</i>
          </div>
          <div className="stats-details">
            <h3>Utilisateurs</h3>
            <div className="stats-numbers">
              <div className="stats-number">
                <span className="number">{stats.totalUsers}</span>
                <span className="label">Total</span>
              </div>
              <div className="stats-number highlight orange">
                <span className="number">{stats.pendingUsers}</span>
                <span className="label">En attente</span>
              </div>
              <div className="stats-number highlight green">
                <span className="number">{stats.activeUsers}</span>
                <span className="label">Actifs</span>
              </div>
            </div>
          </div>
          <Link to="/admin/users" className="stats-link">
            <i className="material-icons">arrow_forward</i>
          </Link>
        </div>

        {/* Carte statistiques soumissions */}
        <div className="stats-card">
          <div className="stats-icon">
            <i className="material-icons">inbox</i>
          </div>
          <div className="stats-details">
            <h3>Soumissions</h3>
            <div className="stats-numbers">
              <div className="stats-number">
                <span className="number">{stats.totalSubmissions}</span>
                <span className="label">Total</span>
              </div>
              <div className="stats-number highlight orange">
                <span className="number">{stats.pendingSubmissions}</span>
                <span className="label">En attente</span>
              </div>
              <div className="stats-number highlight green">
                <span className="number">{stats.approvedSubmissions}</span>
                <span className="label">Approuvées</span>
              </div>
              {/*
              <div className="stats-number highlight red">
                <span className="number">{stats.rejectedSubmissions}</span>
                <span className="label">Rejetées</span>
              </div>
              */}
            </div>
          </div>
          <Link to="/admin/submissions" className="stats-link">
            <i className="material-icons">arrow_forward</i>
          </Link>
        </div>

        {/* Carte statistiques commandes */}
        <div className="stats-card">
          <div className="stats-icon">
            <i className="material-icons">shopping_cart</i>
          </div>
          <div className="stats-details">
            <h3>Commandes</h3>
            <div className="stats-numbers">
              <div className="stats-number">
                <span className="number">{stats.totalOrders}</span>
                <span className="label">Total</span>
              </div>
              <div className="stats-number highlight orange">
                <span className="number">{stats.activeOrders}</span>
                <span className="label">Actives</span>
              </div>
              <div className="stats-number highlight green">
                <span className="number">{stats.sentOrders}</span>
                <span className="label">Expédiées</span>
              </div>
            </div>
          </div>
          <Link to="/admin/orders" className="stats-link">
            <i className="material-icons">arrow_forward</i>
          </Link>
        </div>
      </div>

      <div className="admin-dashboard-grid">
        {/* Carte des dernières soumissions */}
        <div className="admin-card">
          <div className="card-header">
            <h2>Dernières soumissions</h2>
            <Link to="/admin/submissions" className="view-all">
              Voir tout <i className="material-icons">chevron_right</i>
            </Link>
          </div>

          <div className="card-content">
            {submissions?.items?.length > 0 ? (
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nom</th>
                      <th>Utilisateur</th>
                      <th>Date</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.items.map(submission => (
                      <tr key={submission.id}>
                        <td>#{submission.id}</td>
                        <td>{submission.name}</td>
                        <td>
                          <Link to={`/admin/users/${submission.user.id}`}>
                            {submission.user.firstName} {submission.user.lastName}
                          </Link>
                        </td>
                        <td>{formatDateFR(submission.submissionDate)}</td>
                        <td>
                          <span className={`status-badge ${submission.status}`}>
                            {submission.status === 'pending' && 'En attente'}
                            {submission.status === 'approved' && 'Approuvé'}
                            {submission.status === 'rejected' && 'Rejeté'}
                            {submission.status === 'in_progress' && 'En cours'}
                          </span>
                        </td>
                        <td>
                          <div className="table-actions">
                            <Link to={`/admin/submissions/${submission.id}`} className="btn-icon">
                              <i className="material-icons">visibility</i>
                            </Link>
                            {submission.status === 'pending' && (
                              <>
                                <button
                                  className="btn-icon success"
                                  title="Approuver"
                                  onClick={() => handleOpenSubmissionAction(submission, 'approve')}
                                >
                                  <i className="material-icons">check</i>
                                </button>
                                <button
                                  className="btn-icon danger"
                                  title="Rejeter"
                                  onClick={() => handleOpenSubmissionAction(submission, 'reject')}
                                >
                                  <i className="material-icons">close</i>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <i className="material-icons">inbox</i>
                <p>Aucune soumission pour le moment</p>
              </div>
            )}
          </div>
        </div>

        {/* Carte des commandes actives */}
        <div className="admin-card">
          <div className="card-header">
            <h2>Commandes actives</h2>
            <Link to="/admin/orders" className="view-all">
              Voir tout <i className="material-icons">chevron_right</i>
            </Link>
          </div>

          <div className="card-content">
            {orders?.items?.length > 0 ? (
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Soumission</th>
                      <th>Utilisateur</th>
                      <th>Date</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.items
                      .filter(order => ['completed', 'pending', 'accepted', 'in_progress'].includes(order.status))
                      .map(order => (
                        <tr key={order.id}>
                          <td>#{order.id}</td>
                          <td>
                            <Link to={`/admin/submissions/${order.submission.id}`}>
                              #{order.submission.id} - {order.submission.name}
                            </Link>
                          </td>
                          <td>
                            <Link to={`/admin/users/${order.user.id}`}>
                              {order.user.firstName} {order.user.lastName}
                            </Link>
                          </td>
                          <td>{formatDateFR(order.createdAt)}</td>
                          <td>
                            <span className={`status-badge ${order.status}`}>
                              {order.status === 'pending' && 'En attente'}
                              {order.status === 'accepted' && 'Accepté'}
                              {order.status === 'in_progress' && 'En cours'}
                              {order.status === 'completed' && 'Terminé'}
                            </span>
                          </td>
                          <td>
                            <div className="table-actions">
                              <Link to={`/admin/orders/${order.id}`} className="btn-icon">
                                <i className="material-icons">visibility</i>
                              </Link>
                              {order.status === 'pending' && (
                                <button
                                  className="btn-icon success"
                                  title="Accepter"
                                  onClick={() => handleOrderStatusChange(order.id, 'accepted')}
                                >
                                  <i className="material-icons">check</i>
                                </button>
                              )}
                              {order.status === 'accepted' && (
                                <button
                                  className="btn-icon primary"
                                  title="Marquer en cours"
                                  onClick={() => handleOrderStatusChange(order.id, 'in_progress')}
                                >
                                  <i className="material-icons">engineering</i>
                                </button>
                              )}
                              {order.status === 'in_progress' && (
                                <button
                                  className="btn-icon success"
                                  title="Marquer comme terminé"
                                  onClick={() => handleOrderStatusChange(order.id, 'completed')}
                                >
                                  <i className="material-icons">check_circle</i>
                                </button>
                              )}
                              {order.status === 'completed' && (
                                <button
                                  className="btn-icon success"
                                  title="Marquer comme envoyé"
                                  onClick={() => handleOrderStatusChange(order.id, 'sent')}
                                >
                                  <i className="material-icons">local_shipping</i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <i className="material-icons">shopping_cart</i>
                <p>Aucune commande active pour le moment</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal pour les actions sur les soumissions */}
      {showActionModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>
                {actionType === 'approve' ? 'Approuver la soumission' : 'Rejeter la soumission'}
              </h3>
              <button
                className="modal-close"
                onClick={() => setShowActionModal(false)}
              >
                <i className="material-icons">close</i>
              </button>
            </div>

            <div className="modal-body">
              <p>
                {actionType === 'approve'
                  ? 'Vous êtes sur le point d\'approuver cette soumission.'
                  : 'Vous êtes sur le point de rejeter cette soumission.'}
              </p>
              <div className="form-group">
                <label htmlFor="actionReason">Notes (facultatif):</label>
                <textarea
                  id="actionReason"
                  className="form-control"
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  rows="4"
                  placeholder="Ajoutez des notes ou des commentaires..."
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-outline-secondary"
                onClick={() => setShowActionModal(false)}
              >
                Annuler
              </button>

              <button
                className={`btn ${actionType === 'approve' ? 'btn-success' : 'btn-danger'}`}
                onClick={handleSubmissionAction}
              >
                {actionType === 'approve' ? 'Approuver' : 'Rejeter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default AdminDashboard;